import { Avatar } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import {
  QButton,
  QTable,
  QTableHead,
  QTableRow,
  QTableBody,
  QTableCell,
  QTableSortableColumnHeader,
  QSearchBar,
  QAsyncTablePagination,
  QSelect,
} from '@kw/quokka-ui';
import { handleUserSelection } from '../../../utils/handleUserSelection';

// pagination
import { useQPagination } from '../../../kw-ui-components/Pagination/useQPagination';

import { useLocalStorage } from '../../../utils/useLocalStorage';

import { fetchService, useFetchService } from '../../../utils/fetchService';
import { useAppManagementSortParams } from '../useAppManagementSortParams';
import { PageLoader } from '../../../PageLoader';
import { ResetButton } from '../../shared/ResetButton';
import { getComparator } from '../../../utils/getComparator';
import { ManagedAppsTableAddAppPopover } from '../AppManagementPopovers';
import { createQueryString } from '../../../utils/createQueryString';
import { formatPlatform } from '../../../utils/formatPlatform';
import { httpRequest, sendHttpRequest } from '../../../utils/network.service';
import { hasFilterActive, getPlatformFilterValue } from '../../../utils/filterHelpers';

export const useDeniedApps = (queryParams, accessToken) => {
  const queryParamString = createQueryString(queryParams);
  const { data: managedAppsData, error: managedAppsError, mutate } = useFetchService(`/list/denied-apps?${queryParamString}`, accessToken);
  const isLoading = !managedAppsData && !managedAppsError;
  return { managedAppsData, managedAppsError, isLoading, mutate };
};

export default function DeniedAppsTable() {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorData, setVendorData] = useState([]);
  const [apps, setApps] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [addAppButtonAnchorEl, setAddAppButtonAnchorEl] = useState(null);
  const { page, pageSize, handleChangePage, handleChangeRowsPerPage, setPage } = useQPagination(50);

  const [platform, setPlatform] = useState(['all']);
  const [vendor, setVendor] = useState(['all']);

  const {
    appNameOrder,
    toggleAppNameOrder,
    platformOrder,
    togglePlatformOrder,
    vendorOrder,
    toggleVendorOrder,
    versionOrder,
    appIdOrder,
    toggleAppIdOrder,
    numberOfDevicesOrder,
    toggleNumberOfDevicesOrder,
    orderBy,
    setOrderBy,
    resetToggles,
  } = useAppManagementSortParams();

  const [appIcons = {}, updateAppIcons] = useLocalStorage('appIcons', {});

  useEffect(() => {
    const fetchAppMetaData = async () => {
      try {
        if (apps?.length) {
          for (const app of apps) {
            const appIconKey = `${app.bundleId}-${app.os}`;

            if (appIcons[appIconKey]) continue;

            const queryParams = createQueryString({
              packageName: app.bundleId,
              platform: app.os,
              fields: 'iconLargeUrl',
              maxAge: '86400',
            });

            const res = await fetchService(`/application/app-metadata?${queryParams}`, accessToken);

            if (res?.iconLargeUrl && !appIcons[appIconKey]) {
              app.icon = res.iconLargeUrl;
              updateAppIcons((icons: { [key: string]: string }) => ({ ...icons, [appIconKey]: app.icon }));
              break;
            }
          }
        }
      } catch (error) {
        console.log('Error fetching app icon', error);
      }
    };

    fetchAppMetaData();
  }, [accessToken, appIcons, apps, updateAppIcons]);

  const os = getPlatformFilterValue(platform);
  // eslint-disable-next-line prefer-const
  let { managedAppsData, managedAppsError, isLoading, mutate } = useDeniedApps(
    {
      os,
    },
    accessToken,
  );

  const fetchDeviceCounts = async (deniedApps: any[]) => {
    const applications = deniedApps.map(app => ({
      applicationId: app.bundleId,
      os: app.os,
    }));

    const requestBody = { applications };

    try {
      const response = await httpRequest({
        path: `${process.env.DEVICE_SERVICE_URL}/application/device-counts`,
        method: 'POST',
        body: requestBody,
      });

      return response;
    } catch (error) {
      console.error('Error fetching device counts:', error);
      return error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const filteredApps = managedAppsData ? managedAppsData.deniedApps || [] : [];
      const query = searchQuery.toLowerCase().trim();
      const hasPlatformFilter = hasFilterActive(platform);
      const hasVendorFilter = hasFilterActive(vendor);
      const filterConditions = searchQuery.length || hasPlatformFilter || hasVendorFilter;

      let filteredDeniedApps = filterConditions
        ? filteredApps
            .filter(
              item =>
                item.name?.toLowerCase().includes(query) ||
                item.os.toLowerCase().includes(query) ||
                item.vendorName?.toLowerCase().includes(query) ||
                item.bundleId.toLowerCase().includes(query),
            )
            .filter(item => {
              let platformFilter = true;
              let vendorFilter = true;

              if (hasPlatformFilter) {
                platformFilter = platform.includes(item.os);
              }

              if (hasVendorFilter) {
                vendorFilter = vendor.includes(item.vendorName);
              }

              return platformFilter && vendorFilter;
            })
        : filteredApps;

      const { data } = await fetchDeviceCounts(filteredDeniedApps);
      filteredDeniedApps = filteredDeniedApps.map(app => {
        const deviceCountInfo = data.find(count => count.applicationId === app.bundleId);
        return {
          ...app,
          deviceInstallCount: deviceCountInfo ? deviceCountInfo.deviceCount : 0,
        };
      });

      setApps(filteredDeniedApps);
    };
    fetchData();
  }, [managedAppsData, searchQuery, vendor, platform]);

  const total = apps?.length;
  const totalPageCount = Math.ceil(total / pageSize);

  const tableHeaders = [
    {
      title: 'App Name',
      onClick: toggleAppNameOrder,
      sortValue: appNameOrder,
    },
    {
      title: 'Platform',
      onClick: togglePlatformOrder,
      sortValue: platformOrder,
    },
    {
      title: 'Vendor',
      onClick: toggleVendorOrder,
      sortValue: vendorOrder,
    },
    {
      title: 'App ID',
      onClick: toggleAppIdOrder,
      sortValue: appIdOrder,
    },
    {
      title: '# of Devices',
      onClick: toggleNumberOfDevicesOrder,
      sortValue: numberOfDevicesOrder,
    },
    {
      title: 'Action',
      onClick: () => {},
      sortValue: null,
    },
  ];

  // column sorting UI filters
  const order = tableHeaders.map(({ sortValue }) => sortValue).find(o => o);

  useEffect(() => {
    if (appNameOrder) {
      setOrderBy('name');
    }

    if (platformOrder) {
      setOrderBy('platform');
    }

    if (vendorOrder) {
      setOrderBy('vendorName');
    }

    if (versionOrder) {
      setOrderBy('version');
    }

    if (appIdOrder) {
      setOrderBy('packageName');
    }

    if (numberOfDevicesOrder) {
      setOrderBy('deviceInstallCount');
    }

    if (!order) {
      setOrderBy(null);
    }
  }, [numberOfDevicesOrder, order, orderBy, setOrderBy, appNameOrder, platformOrder, vendorOrder, versionOrder, appIdOrder]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, setPage]);

  const platformSelectData = [
    { value: 'all', label: 'Both' },
    { value: 'android', label: 'Android' },
    { value: 'ios', label: 'iOS' },
  ];

  const vendorSelectData = useRef([]);
  useEffect(() => {
    const appData = managedAppsData ? managedAppsData.deniedApps || [] : [];
    vendorSelectData.current = [
      { value: 'all', label: 'All' },
      ...appData.map(({ vendorName }) => ({ value: vendorName, label: vendorName })),
    ];
    setVendorData([...new Map(vendorSelectData.current.map(item => [item.value, item])).values()]);
  }, [apps, managedAppsData]);

  const resetFilterAndSorts = () => {
    resetToggles();
    setPage(0);
    setPlatform(['all']);
    setVendor(['all']);
    setSearchQuery('');
  };

  const handleDeleteDeniedApp = async (app: any) => {
    const successMessage = `Successfully deleted ${app.name}`;
    const errorMessage = `Error deleting ${app.name}`;

    await sendHttpRequest({
      path: `${process.env.LIST_SERVICE_URL}/list/denied-apps/${app.bundleId}/${app.os}`,
      method: 'DELETE',
      successMessage,
      errorMessage,
      enqueueSnackbar,
    });

    await mutate();
  };

  const handleAddAppPopoverClick = event => {
    setAddAppButtonAnchorEl(addAppButtonAnchorEl ? null : event.currentTarget);
  };

  if (managedAppsError) {
    return <div>failed to load</div>;
  }

  return (
    <>
      {isLoading && <PageLoader />}
      <StyledDeniedAppsTableInfoWrapper>
        <span>
          Create a list of apps you wish to deny in your environment. Once complete, create a policy in the Policies page using the “Denied
          App Installed” policy rule to enforce this list. If an app from your Denied Apps list is installed on an enrolled device in your
          environment, the device will be in violation of your Policy. Remove an app from this list before adding it to a different list.
        </span>
      </StyledDeniedAppsTableInfoWrapper>
      <StyledFilterWrapper>
        <QSelect
          multiple
          label="Platform"
          options={platformSelectData}
          value={platform}
          onChange={e => {
            handleUserSelection(platform, e.target.value, setPlatform);
            setPage(0);
          }}
          formControlProps={{ sx: { width: 110 } }}
        />
        <QSelect
          multiple
          label="Vendor"
          options={vendorData}
          value={vendor}
          onChange={e => {
            handleUserSelection(vendor, e.target.value, setVendor);
            setPage(0);
          }}
          formControlProps={{ sx: { width: 110 } }}
        />
        <ResetButton
          onClick={() => {
            resetFilterAndSorts();
          }}
        />
        <StyledQButton endIcon={<KeyboardArrowDownIcon />} variant="filled" onClick={event => handleAddAppPopoverClick(event)}>
          Add Denied App
        </StyledQButton>
        <ManagedAppsTableAddAppPopover
          anchorEl={addAppButtonAnchorEl}
          setAnchorEl={setAddAppButtonAnchorEl}
          handleClose={event => handleAddAppPopoverClick(event)}
        />
      </StyledFilterWrapper>
      <StyledTableWrapper>
        <QSearchBar
          searchValue={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search apps"
          onKeyPress={e => {
            if (e.key === 'Enter') {
              setSearchQuery(e.target.value.toString());
              setPage(0);
            }
          }}
          onRemove={() => {
            setSearchQuery('');
            setPage(0);
          }}
        />

        {total > 10 ? (
          <QAsyncTablePagination
            page={page + 1}
            pagination={{
              dataPerPage: pageSize,
              totalData: total,
              lastPage: totalPageCount,
            }}
            getPage={newPage => setPage(newPage - 1)}
            showRowsPerPage={true}
            currentRows={(page + 1) * pageSize > apps.length ? apps.length % pageSize : pageSize}
            handlePageSizeChange={handleChangeRowsPerPage}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
          />
        ) : null}
        <QTable aria-label="Managed-Apps-Table">
          <QTableHead>
            <QTableRow>
              {tableHeaders.map(({ title, onClick, sortValue }) => (
                <QTableSortableColumnHeader
                  key={title}
                  title={title}
                  onClick={() => {
                    onClick();
                    setPage(0);
                  }}
                  sortValue={sortValue}
                />
              ))}
            </QTableRow>
          </QTableHead>
          <QTableBody>
            {[...apps]
              .sort(getComparator(order, orderBy))
              .slice(page * pageSize, (page + 1) * pageSize)
              .map(app => {
                const localIconUrl = appIcons[`${app.bundleId}-${app.os}`];

                return (
                  <QTableRow key={app.bundleId} aria-label={`${app.name} row`}>
                    <StyledKwTableCell>
                      <StyledAvatar alt={`${app.name} app icon`} src={localIconUrl} />
                      {app.name}
                    </StyledKwTableCell>
                    <QTableCell>{formatPlatform(app.os)}</QTableCell>
                    <QTableCell>{app.vendorName}</QTableCell>
                    <QTableCell>{app.bundleId}</QTableCell>
                    <QTableCell>{app.deviceInstallCount}</QTableCell>
                    <QTableCell>
                      <QButton size="medium" onClick={event => handleDeleteDeniedApp(app)}>
                        <DeleteOutlineIcon />
                      </QButton>
                    </QTableCell>
                  </QTableRow>
                );
              })}
          </QTableBody>
        </QTable>
        {total > 10 ? (
          <QAsyncTablePagination
            page={page + 1}
            pagination={{
              dataPerPage: pageSize,
              totalData: total,
              lastPage: totalPageCount,
            }}
            getPage={newPage => setPage(newPage - 1)}
            showRowsPerPage={true}
            currentRows={(page + 1) * pageSize > apps.length ? apps.length % pageSize : pageSize}
            handlePageSizeChange={handleChangeRowsPerPage}
            pageSize={pageSize}
            pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
          />
        ) : null}
      </StyledTableWrapper>
    </>
  );
}

const StyledTableWrapper = styled('div')`
  width: 100%;
`;

const StyledQButton = styled(QButton)`
  // align-self: flex-end;
  margin-left: auto;
  min-width: 110px;
`;

const StyledAvatar = styled(Avatar)`
  margin-right: 10px;
`;

const StyledKwTableCell = styled(QTableCell)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledFilterWrapper = styled('div')`
  display: flex;
  column-gap: 16px;
  margin-bottom: 28px;
  margin-top: 28px;
  align-items: center;
`;

const StyledDeniedAppsTableInfoWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 28px;
`;
